/**
 * useCustomerInvoiceAttachments
 *
 * Provides a `fetchAttachments` callback compatible with BulkEmailModal
 * that fetches outstanding invoice transaction IDs for a customer via
 * POST /api/v1/customers/outstanding-invoices, then downloads each
 * invoice document as a base64-encoded attachment.
 */

import { useCallback } from "react";
import { useApi } from "@/hooks/useApi";
import { useTransactionDocumentDownload } from "@/hooks/useTransactionDocumentDownload";
import { toast } from "sonner";
import type { BulkEmailRecipient } from "@/components/shared/bulk-email-modal";
import type { UploadedFile } from "@/lib/email-placeholder-utils";

interface OutstandingInvoiceSummary {
  transactionId: number;
  invoiceNumber: string;
  outstandingAmount: number;
}

interface CustomerOutstandingInvoices {
  customerId: number;
  customerName: string;
  invoices: OutstandingInvoiceSummary[];
}

interface BatchOutstandingInvoicesResponse {
  customers: CustomerOutstandingInvoices[];
  totalInvoiceCount: number;
}

export function useCustomerInvoiceAttachments() {
  const { apiPost } = useApi();
  const { downloadDocumentAsBase64 } = useTransactionDocumentDownload();

  const fetchAttachments = useCallback(
    async (recipient: BulkEmailRecipient): Promise<UploadedFile[]> => {
      const customerId = Number(recipient.id);

      const response = await apiPost<BatchOutstandingInvoicesResponse>(
        "/api/v1/customers/outstanding-invoices",
        { customerIds: [customerId] },
      );

      const customerData = response.customers.find(
        (x) => x.customerId === customerId,
      );
      if (!customerData || customerData.invoices.length === 0) return [];

      const transactionIds = customerData.invoices.map(
        (x) => x.transactionId,
      );

      const results = await Promise.allSettled(
        transactionIds.map(async (txId) => {
          const doc = await downloadDocumentAsBase64(txId);
          return {
            name: doc.fileName,
            dataUrl: "",
            contentType: doc.contentType,
            base64Content: doc.base64Content,
          } satisfies UploadedFile;
        }),
      );

      const successful = results
        .filter(
          (r): r is PromiseFulfilledResult<UploadedFile> =>
            r.status === "fulfilled",
        )
        .map((r) => r.value);

      const failedCount = results.filter(
        (r) => r.status === "rejected",
      ).length;
      if (failedCount > 0) {
        toast.warning(
          `${failedCount} invoice document(s) could not be downloaded`,
        );
      }

      return successful;
    },
    [apiPost, downloadDocumentAsBase64],
  );

  return fetchAttachments;
}
